import {
  Settings,
  Cpu,
  Zap,
  Building,
  Network,
  BookOpen,
  History,
  Bookmark,
  MessageSquare,
  ChevronRight,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { HubShell } from '@/components/layout/HubShell';
import { useUserRole } from '@/stores/authStore';
import { SettingsPage } from './SettingsPage';
import { AIProviderSettingsPage } from './AIProviderSettingsPage';
import { KeyboardShortcutsPage } from './KeyboardShortcutsPage';
import EnterpriseDashboard from './EnterpriseDashboard';
import SessionsPage from './SessionsPage';
import BookmarksPage from './BookmarksPage';
import { CommentingSystemPage } from './CommentingSystemPage';
import { ExtensionSettingsPage } from './ExtensionSettingsPage';

/**
 * Settings hub — the place for app configuration, account, agentic, and skills.
 *
 * Sub-routes (kept at the same path level):
 *   /settings            → this hub
 *   /settings/agentic    → Agentic Orchestration Center (full page)
 *   /settings/skills     → Skill Library (full page)
 */
export function SettingsHubPage() {
  const role = useUserRole();
  const tabs = [
    { id: 'general', label: 'General', icon: Settings, render: () => <SettingsPage /> },
    // Provider keys are shared by every user, so only admins can see or change them.
    ...(role === 'admin'
      ? [{ id: 'ai-providers', label: 'AI Providers', icon: Cpu, render: () => <AIProviderSettingsPage /> }]
      : []),
    { id: 'shortcuts', label: 'Shortcuts', icon: Zap, render: () => <KeyboardShortcutsPage /> },
    { id: 'sessions', label: 'Sessions', icon: History, render: () => <SessionsPage /> },
    { id: 'bookmarks', label: 'Bookmarks', icon: Bookmark, render: () => <BookmarksPage /> },
    { id: 'comments', label: 'Comments', icon: MessageSquare, render: () => <CommentingSystemPage /> },
    { id: 'extension', label: 'Extension', icon: Zap, render: () => <ExtensionSettingsPage /> },
    { id: 'enterprise', label: 'Enterprise', icon: Building, render: () => <EnterpriseDashboard /> },
  ];

  return (
    <HubShell
      title="Settings"
      description="App preferences, account, AI providers, agentic orchestration, and skills."
      icon={Settings}
      tabs={tabs}
      defaultSection="general"
      banner={
        <div className={`grid gap-3 ${role === 'admin' ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2'}`}>
          <Link
            to="/settings/agentic"
            className="group flex items-center gap-3 rounded-2xl border border-khiimori-border bg-khiimori-bg-elevated/40 p-4 backdrop-blur-xl transition-all hover:border-primary-500/40 hover:bg-khiimori-bg-elevated/70"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500/30 to-fuchsia-500/30">
              <Network className="h-5 w-5 text-khiimori-text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-khiimori-text-primary">Agentic Orchestration</div>
              <div className="text-[11px] text-khiimori-text-secondary/50">Run the autonomous agent pass</div>
            </div>
            <ChevronRight className="h-4 w-4 text-khiimori-text-secondary/40 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/settings/skills"
            className="group flex items-center gap-3 rounded-2xl border border-khiimori-border bg-khiimori-bg-elevated/40 p-4 backdrop-blur-xl transition-all hover:border-primary-500/40 hover:bg-khiimori-bg-elevated/70"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500/30 to-rose-500/30">
              <BookOpen className="h-5 w-5 text-khiimori-text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-khiimori-text-primary">Skill Library</div>
              <div className="text-[11px] text-khiimori-text-secondary/50">Every agent capability, in one place</div>
            </div>
            <ChevronRight className="h-4 w-4 text-khiimori-text-secondary/40 transition-transform group-hover:translate-x-1" />
          </Link>
          {role === 'admin' ? (
            <Link
              to="/settings/admin/users"
              className="group flex items-center gap-3 rounded-2xl border border-khiimori-border bg-khiimori-bg-elevated/40 p-4 backdrop-blur-xl transition-all hover:border-primary-500/40 hover:bg-khiimori-bg-elevated/70"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500/30 to-cyan-500/30">
                <Users className="h-5 w-5 text-khiimori-text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-khiimori-text-primary">User Management</div>
                <div className="text-[11px] text-khiimori-text-secondary/50">Admin only — roles, access, audit</div>
              </div>
              <ChevronRight className="h-4 w-4 text-khiimori-text-secondary/40 transition-transform group-hover:translate-x-1" />
            </Link>
          ) : null}
        </div>
      }
    />
  );
}

export default SettingsHubPage;
